import { Field } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  IQuoteSettingProcessingSchema,
  QuoteSettingProcessingSchema,
  QuoteStartDateSourceEnum,
} from '@monetize/types/app';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { handleApiErrorToast } from '../../../../../api/axios';
import {
  MAccordion,
  MAccordionCustomButton,
  MAlert,
  MBox,
  MButton,
  MFlex,
  MFormField,
  MRadio,
  MRadioGroup,
  MSwitch,
  MText,
} from '../../../../../components/Monetize';
import { MSettingAccordionItem } from '../../../../../components/Monetize/MSettingAccordionItem';
import { QUOTE_START_DATE_SOURCE_OPTIONS } from '../../../../../constants/quotes';
import { useAccordionControl } from '../../../../../hooks';
import {
  getHighlightStyles,
  useSettingsSectionHighlight,
} from '../../../../../hooks/useSettingsSectionHighlight';
import { SettingsSection } from '../../../components/SettingsSection';
import { QUOTE_SETTING_KEYS } from '../quoteSettingsConfig';

interface QuoteSettingProcessingProps {
  value: IQuoteSettingProcessingSchema;
  onSubmit: (data: IQuoteSettingProcessingSchema) => Promise<void>;
  isLoading?: boolean;
}

export const QuoteProcessingSettings = ({
  value,
  onSubmit,
  isLoading,
}: QuoteSettingProcessingProps) => {
  const isProcessingHighlighted = useSettingsSectionHighlight(
    QUOTE_SETTING_KEYS.quoteProcessing,
  );
  const isBehaviorHighlighted = useSettingsSectionHighlight(
    QUOTE_SETTING_KEYS.quoteProcessingBehavior,
  );
  const isStartDateHighlighted = useSettingsSectionHighlight(
    QUOTE_SETTING_KEYS.quoteProcessingStartDate,
  );
  const shouldOpen =
    isProcessingHighlighted || isBehaviorHighlighted || isStartDateHighlighted;

  const quoteSettingProcessingForm = useForm<IQuoteSettingProcessingSchema>({
    resolver: zodResolver(QuoteSettingProcessingSchema),
    mode: 'onChange',
    defaultValues: value,
    values: value,
  });
  const {
    handleSubmit,
    control,
    formState: { errors, isDirty, isValid },
    reset,
    watch,
  } = quoteSettingProcessingForm;

  useEffect(() => {
    if (value) {
      reset({
        ...value,
      });
    }
  }, [value, reset]);

  const onSave = async (data: IQuoteSettingProcessingSchema) => {
    try {
      await onSubmit({
        ...data,
        defaultStartDateSource: pauseAtAcceptedChangedFromTrueToFalse
          ? QuoteStartDateSourceEnum.ORIGINAL_CONTRACT_START
          : data.defaultStartDateSource,
      });
    } catch (err) {
      handleApiErrorToast(err);
    }
  };
  const pauseAtAcceptedWatch = watch('pauseAtAccepted');

  const pauseAtAcceptedChangedFromTrueToFalse =
    !pauseAtAcceptedWatch && value.pauseAtAccepted;

  const { accordionValue, handleAccordionChange, setAccordionValue } =
    useAccordionControl();

  useEffect(() => {
    if (shouldOpen) {
      setAccordionValue(['0']);
    }
  }, [shouldOpen, setAccordionValue]);

  return (
    <MAccordion
      multiple
      data-testid="quote-setting-processing"
      w="100%"
      value={accordionValue}
      onValueChange={({ value }) => handleAccordionChange(value)}
    >
      <form onSubmit={handleSubmit(onSave)}>
        <MSettingAccordionItem
          value="0"
          id={QUOTE_SETTING_KEYS.quoteProcessing}
          {...getHighlightStyles(isProcessingHighlighted)}
          renderAccordionButton={({ isExpanded }) => (
            <MAccordionCustomButton
              isExpanded={isExpanded}
              label="Quote Processing"
            />
          )}
          borderWidth="1px"
        >
          <MBox mt="4">
            <SettingsSection
              label="Quote Processing Behavior"
              id={QUOTE_SETTING_KEYS.quoteProcessingBehavior}
              {...getHighlightStyles(isBehaviorHighlighted)}
            >
              <MFormField
                error={errors?.allowQuoteLevelOverrideOfStartDateSource}
                onClick={(ev) => ev.stopPropagation()}
                w="auto"
              >
                <Controller
                  name="pauseAtAccepted"
                  control={control}
                  render={({ field: { value, onChange, ...rest } }) => (
                    <Field.Root orientation="horizontal" gap="4">
                      <Field.Label
                        htmlFor="pauseAtAccepted"
                        fontWeight="400"
                        mb="0"
                        flex="1"
                      >
                        Quotes automatically move from Accepted to Processed.
                      </Field.Label>
                      <MSwitch
                        size="md"
                        isChecked={!value}
                        id="pauseAtAccepted"
                        aria-describedby={`pauseAtAccepted-subheading`}
                        onChange={(event) => {
                          onChange(!event.target.checked);
                        }}
                      />
                    </Field.Root>
                  )}
                />
              </MFormField>

              {pauseAtAcceptedChangedFromTrueToFalse && (
                <MAlert
                  type="warning"
                  bg="tOrange.input"
                  mt={2}
                  alertDescriptionProps={{ fontSize: 'sm' }}
                >
                  Enabling the feature may disrupt the processing of quotes
                  configured with delayed billing and currently in Accepted
                  status. This will automatically set the default contract start
                  to "Editable until Drafted".
                </MAlert>
              )}
            </SettingsSection>

            <SettingsSection
              label="Start Date"
              id={QUOTE_SETTING_KEYS.quoteProcessingStartDate}
              {...getHighlightStyles(isStartDateHighlighted)}
            >
              <MText mb="2">Select the default contract start behavior: </MText>
              <MFormField
                error={errors?.defaultStartDateSource}
                onClick={(ev) => ev.stopPropagation()}
                w="auto"
              >
                <Controller
                  name="defaultStartDateSource"
                  control={control}
                  render={({ field: { value, onChange, ...rest } }) => (
                    <MRadioGroup
                      onChange={(val) => onChange(val ?? '')}
                      value={value || ''}
                      rowGap={2}
                      isDisabled={!pauseAtAcceptedWatch}
                      display="flex"
                      flexDir="column"
                    >
                      {QUOTE_START_DATE_SOURCE_OPTIONS.map(
                        ({ value: optionValue, title }) => (
                          <MRadio value={optionValue} key={optionValue} mt="1">
                            {title}
                          </MRadio>
                        ),
                      )}
                    </MRadioGroup>
                  )}
                />
              </MFormField>

              <MFormField
                error={errors?.allowQuoteLevelOverrideOfStartDateSource}
                onClick={(ev) => ev.stopPropagation()}
                w="auto"
                mt="6"
              >
                <Controller
                  name="allowQuoteLevelOverrideOfStartDateSource"
                  control={control}
                  render={({ field: { value, onChange, ...rest } }) => (
                    <Field.Root orientation="horizontal" gap="4">
                      <Field.Label
                        htmlFor="allowQuoteLevelOverrideOfStartDateSource"
                        fontWeight="400"
                        mb="0"
                        flex="1"
                      >
                        Allow this setting to be editable on Quotes
                      </Field.Label>
                      <MSwitch
                        size="md"
                        isChecked={!!value}
                        isDisabled={!pauseAtAcceptedWatch}
                        id="allowQuoteLevelOverrideOfStartDateSource"
                        aria-describedby={`allowQuoteLevelOverrideOfStartDateSource-subheading`}
                        onChange={(event) => {
                          onChange(event.target.checked);
                        }}
                      />
                    </Field.Root>
                  )}
                />
              </MFormField>
            </SettingsSection>

            <MFlex justify="end">
              <MButton
                variant="primary"
                onClick={handleSubmit(onSave)}
                disabled={!isDirty || !isValid || isLoading}
                isLoading={isLoading}
              >
                Save
              </MButton>
            </MFlex>
          </MBox>
        </MSettingAccordionItem>
      </form>
    </MAccordion>
  );
};
